import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, Search, SendHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { toast } from "sonner";

import type { LocationInput } from "@mandoob/shared";

import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";

// OpenStreetMap-based location picker (no API key): Leaflet map with official
// OSM tiles, tap/drag to place the pin, Nominatim for search and best-effort
// reverse geocoding. Replaces the Google Places picker for now — Google Maps
// can slot back in later behind the same props.

const DEFAULT_CENTER: L.LatLngTuple = [24.7136, 46.6753]; // Riyadh
const DEFAULT_ZOOM = 11;

interface PickedPlace {
  lat: number;
  lng: number;
  name?: string;
  address?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  name?: string;
  display_name?: string;
}

// Brand-colored pin as a divIcon — Leaflet's default marker PNGs don't
// survive bundling without asset gymnastics.
const pinIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="var(--brand)" stroke="var(--background)" stroke-width="1.5"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3" fill="var(--background)" stroke="none"/></svg>`,
  iconSize: [32, 32],
  iconAnchor: [16, 30],
});

async function reverseGeocode(
  lat: number,
  lng: number,
  language: string,
): Promise<string | undefined> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${language}`,
    );
    if (!res.ok) return undefined;
    const data = (await res.json()) as { display_name?: string };
    return data.display_name || undefined;
  } catch {
    return undefined;
  }
}

export function LocationDialog({
  open,
  onClose,
  onSend,
}: {
  open: boolean;
  onClose: () => void;
  onSend: (location: LocationInput) => void;
}) {
  const { t, dir, language } = useLanguage();
  const [picked, setPicked] = useState<PickedPlace | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 450);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [locating, setLocating] = useState(false);

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const pickedRef = useRef<PickedPlace | null>(null);
  pickedRef.current = picked;

  /** Places/moves the pin and (best-effort) reverse-geocodes an address. */
  const placePin = (
    lat: number,
    lng: number,
    name?: string,
    address?: string,
  ) => {
    const map = mapRef.current;
    if (!map) return;
    if (!markerRef.current) {
      markerRef.current = L.marker([lat, lng], {
        icon: pinIcon,
        draggable: true,
      }).addTo(map);
      markerRef.current.on("dragend", () => {
        const pos = markerRef.current!.getLatLng();
        placePin(pos.lat, pos.lng);
      });
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }
    setPicked({ lat, lng, name, address });
    if (!address) {
      void reverseGeocode(lat, lng, language).then((resolved) => {
        // Only attach if the pin hasn't moved meanwhile.
        const current = pickedRef.current;
        if (resolved && current && current.lat === lat && current.lng === lng) {
          setPicked({ ...current, address: resolved });
        }
      });
    }
  };

  // Leaflet lifecycle: build the map when the dialog opens, tear it down on
  // close (the dialog unmounts its content, so refs must reset too).
  useEffect(() => {
    if (!open) return;
    // Next frame so the dialog's layout has real dimensions.
    const raf = requestAnimationFrame(() => {
      const div = mapDivRef.current;
      if (!div || mapRef.current) return;
      const map = L.map(div, { zoomControl: true }).setView(
        DEFAULT_CENTER,
        DEFAULT_ZOOM,
      );
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(map);
      map.on("click", (event: L.LeafletMouseEvent) => {
        placePin(event.latlng.lat, event.latlng.lng);
      });
      mapRef.current = map;
    });
    return () => {
      cancelAnimationFrame(raf);
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // placePin is stable enough for this lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Nominatim search for the debounced query.
  useEffect(() => {
    const query = debouncedSearch.trim();
    if (!open || query.length < 3) {
      setResults([]);
      return;
    }
    let cancelled = false;
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&accept-language=${language}&q=${encodeURIComponent(query)}`,
    )
      .then((res) => (res.ok ? res.json() : []))
      .then((data: NominatimResult[]) => {
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, language]);

  const selectResult = (result: NominatimResult) => {
    const lat = Number(result.lat);
    const lng = Number(result.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setResults([]);
    setSearch("");
    mapRef.current?.flyTo([lat, lng], 16, { duration: 0.6 });
    placePin(lat, lng, result.name || undefined, result.display_name);
  };

  const detectCurrentLocation = async () => {
    setLocating(true);
    try {
      const options = { enableHighAccuracy: true, timeout: 10_000 };
      const viaWebView = () => {
        if (!navigator.geolocation) {
          throw new Error("GeoUnsupported");
        }
        return new Promise<GeolocationCoordinates>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position.coords),
            reject,
            options,
          ),
        );
      };
      let coords: Pick<GeolocationCoordinates, "latitude" | "longitude">;
      if (isNative()) {
        // WebView geolocation is unreliable in the shells — the native
        // plugin owns the runtime permission prompt and the GPS itself.
        // The Android plugin is built on Google's fused-location client, so
        // on phones without Google services (Huawei/AppGallery) it fails; the
        // WebView's own geolocation still works there, so fall back to it.
        try {
          const { Geolocation } = await import("@capacitor/geolocation");
          coords = (await Geolocation.getCurrentPosition(options)).coords;
        } catch {
          coords = await viaWebView();
        }
      } else {
        try {
          coords = await viaWebView();
        } catch (error) {
          if (error instanceof Error && error.message === "GeoUnsupported") {
            toast.error(t("Mandoob:Location.GeoUnsupported"));
            return;
          }
          throw error;
        }
      }
      const { latitude, longitude } = coords;
      mapRef.current?.flyTo([latitude, longitude], 16, { duration: 0.6 });
      placePin(latitude, longitude);
    } catch {
      toast.error(t("Mandoob:Location.GeoFailed"));
    } finally {
      setLocating(false);
    }
  };

  const handleClose = () => {
    setPicked(null);
    setSearch("");
    setResults([]);
    onClose();
  };

  const handleSend = () => {
    if (!picked) return;
    onSend({
      latitude: picked.lat,
      longitude: picked.lng,
      name: picked.name || undefined,
      address: picked.address || undefined,
    });
    handleClose();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      title={t("Mandoob:Location.Title")}
      contentClassName="tw:sm:max-w-2xl"
      footer={
        <div dir={dir} className="tw:flex tw:w-full tw:items-center tw:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void detectCurrentLocation()}
            loading={locating}
            className="tw:rounded-full"
          >
            <LocateFixed className="tw:size-4" strokeWidth={1.75} />
            {t("Mandoob:Location.UseCurrent")}
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={!picked}
            className="tw:ms-auto tw:rounded-full"
          >
            {t("Mandoob:Location.Send")}
            <SendHorizontal className="tw:size-4 tw:rtl:-scale-x-100" />
          </Button>
        </div>
      }
    >
      {open ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:relative">
            <Search className="tw:pointer-events-none tw:absolute tw:start-3 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Mandoob:Location.SearchPlaceholder")}
              className="tw:ps-9"
            />
            {results.length > 0 ? (
              <ul className="tw:absolute tw:inset-x-0 tw:top-full tw:z-[1000] tw:m-0 tw:mt-1 tw:max-h-56 tw:list-none tw:overflow-y-auto tw:rounded-xl tw:border tw:border-border tw:bg-popover tw:p-1 tw:shadow-md">
                {results.map((result, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() => selectResult(result)}
                      className="tw:w-full tw:rounded-lg tw:px-2.5 tw:py-2 tw:text-start tw:text-sm tw:text-foreground tw:hover:bg-muted"
                    >
                      <span className="tw:line-clamp-2">
                        {result.display_name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {/* Leaflet controls assume LTR internals — keep the map itself LTR. */}
          <div
            ref={mapDivRef}
            dir="ltr"
            className="tw:h-72 tw:w-full tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:sm:h-80"
          />

          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {picked?.address ?? t("Mandoob:Location.ManualHint")}
          </p>
        </div>
      ) : null}
    </ResponsiveDialog>
  );
}
